import React from 'react';
import { useTranslation } from 'react-i18next';
import numeral from 'numeral';
import { PapiAdoptable, PapiDeviation } from '@wix/da-papi-types';
import PromoBannerMobile from '@wix/da-ds/pkg/PromoBannerMobile';
import Countdown from '@wix/da-ds/pkg/Time/Countdown';
import { formatDollars } from '../../../utils/formatting';
import {
  getHasOffers,
  getIsBeforeOfferEndTime,
} from '../../../utils/adoptables';
import s from './OfferBannerMobile.scss';

export type Props = Partial<PapiAdoptable['offers']> & {
  className?: string;
  deviation: PapiDeviation;
};

const OfferBannerMobile: React.FC<Props> = ({
  className,
  minOffer,
  numOffers,
  highestOffer,
  offerEndTime,
  ownOffer,
  deviation,
}) => {
  const { t } = useTranslation();
  const hasOffers = !!numOffers;
  const noOffers = !numOffers;
  const ownOfferDollarPrice = ownOffer?.dollarPrice;

  if (!(getHasOffers(deviation) || getIsBeforeOfferEndTime(deviation))) {
    return null;
  }

  return (
    <PromoBannerMobile
      className={className}
      variant="adoptables"
      message={
        <div className={s['message']}>
          {noOffers && (
            <span>
              {t('offerBanner.startingPrice.mobile', {
                postProcess: ['reactPostprocessor', 'featureBrancher'],
                price: <b>{formatDollars(minOffer)}</b>,
              })}
            </span>
          )}
          {hasOffers && (
            <>
              {!!offerEndTime && (
                <span>
                  <Countdown
                    className={s['countdown']}
                    prefix={t('offerBanner.endsCountdown.mobile')}
                    countToDate={offerEndTime}
                    endedMessage={t('offerBanner.saleEnded')}
                    endedClassName={s['countdown-ended']}
                    // Matching the countdown we have in adoptables
                    renderTimer={(d, h, m, sec) => (
                      <span className={s['countdown-value']}>
                        {[h + d * 24, m, sec]
                          .map(n => numeral(n).format('00'))
                          .join(':')}
                      </span>
                    )}
                  />
                </span>
              )}
              <span>
                {t('offerBanner.highestOffer.mobile', {
                  postProcess: ['reactPostprocessor', 'featureBrancher'],
                  price: <b>{formatDollars(highestOffer)}</b>,
                })}
              </span>
              <br />
              <span className={s['num-offers']}>
                {t('offerBanner.numOffers.mobile', {
                  postProcess: ['reactPostprocessor', 'featureBrancher'],
                  offers: <b>{`${numOffers}`}</b>,
                })}
              </span>
              {!!ownOfferDollarPrice && (
                <span>
                  {t('offerBanner.myOffer.mobile', {
                    postProcess: ['reactPostprocessor', 'featureBrancher'],
                    price: <b>{formatDollars(ownOfferDollarPrice)}</b>,
                  })}
                </span>
              )}
            </>
          )}
        </div>
      }
    />
  );
};
OfferBannerMobile.displayName = 'OfferBannerMobile';

export default OfferBannerMobile;
